import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@material-ui/core';
import '../cmpMemberPages.css';
import {
  useCompleteMyCmpMemberCardUpdateMutation,
  useGetMyCmpMemberAutoRenewalQuery,
  useSetMyCmpMemberAutoRenewalMutation,
  useStartMyCmpMemberCardUpdateMutation,
} from '../../../../graphql/types';
import { getStripeClient, StripeCardElement, StripeClient } from './stripeJs';
import { getUserFacingErrorText } from '../../../common/utilities/userFacingError';

// Wave C ACON-3 (Member Dashboard Invoices ACC:L1106-1110, card modal
// ACC:L1516-1540; D15, G-12): "Select Auto Renewal" and "Update Credit Card
// Information". The checkbox enables the button. Auto-renewal turns on when
// a card is saved (or at once when a card is already on file); unticking
// turns it off and keeps the card.

export const AUTO_RENEWAL_TEXT = 'to automatically renew your membership each year by credit card, ensuring your company continues to enjoy valuable Chamber benefits and opportunities. By selecting this option, you authorize the payment processor to securely save your credit card information and charge it for future annual membership renewals.';
export const CARD_FIELDS_INCOMPLETE = 'Complete all card fields.';
const BLUE = '#0D8BF2';
const RED = '#ED1C24';
const TEXT = '#0A0A0A';
const operationContext = { preserveErrorForCaller: true };
// Wave 1 (Oct 19 2026): raw Stripe.js and browser text never shows.
export const AUTO_RENEWAL_ERROR_TEXT = {
  startFailed: 'The card form did not open. Close this window and try again.',
  cardNotSaved: 'We could not save this card. Your saved card did not change. Check the card details and try again, or use a different card.',
  saveFailed: 'We could not save your card. Try again.',
  toggleFailed: 'We could not change auto renewal. Refresh the page and try again.',
};
const operationId = () => `card-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
const fieldBox: React.CSSProperties = { border: '1px solid #CFCFCF', borderRadius: 8, padding: '11px 12px', fontFamily: "'DM Sans', sans-serif",
  fontSize: 14, color: TEXT, width: '100%', boxSizing: 'border-box', outline: 'none', background: '#FFFFFF' };
const smallLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: TEXT, marginBottom: 4 };
const cardStyle = { style: { base: { color: TEXT, fontFamily: '"DM Sans", sans-serif', fontSize: '14px', '::placeholder': { color: '#9E9E9E' } }, invalid: { color: '#F21B1B' } } };

type CardKey = 'number' | 'expiry' | 'cvc';

const CardModal = ({ memberName, cardOnFile, turnOnAutoRenewal, onClose, onSaved }: {
  memberName: string; cardOnFile?: string | null; turnOnAutoRenewal: boolean; onClose: () => void; onSaved: () => void;
}) => {
  const [start] = useStartMyCmpMemberCardUpdateMutation({ context: operationContext });
  const [complete] = useCompleteMyCmpMemberCardUpdateMutation({ context: operationContext });
  const [session, setSession] = useState<{ clientSecret: string; stripe: StripeClient }>();
  const [name, setName] = useState('');
  const [done, setDone] = useState<Record<CardKey, boolean>>({ number: false, expiry: false, cvc: false });
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const fieldRefs = { number: useRef<HTMLDivElement>(null), expiry: useRef<HTMLDivElement>(null), cvc: useRef<HTMLDivElement>(null) };
  const cardNumber = useRef<StripeCardElement>();
  // Stripe confirmed the card, but CMP did not save it yet: a retry only saves.
  const confirmedSetupIntent = useRef<string>();

  useEffect(() => {
    let active = true;
    const mounted: StripeCardElement[] = [];
    start({ variables: { input: { operationId: operationId() } } }).then(async result => {
      const value = result.data?.startMyCmpMemberCardUpdate;
      if (!value) throw new Error(AUTO_RENEWAL_ERROR_TEXT.startFailed);
      const stripe = await getStripeClient(value.publishableKey, value.connectedAccountId);
      if (!active) return;
      const elements = stripe.elements();
      ([['number', 'cardNumber', '1234 1234 1234 1234'], ['expiry', 'cardExpiry', 'MM / YY'], ['cvc', 'cardCvc', 'CVC']] as const).forEach(([key, type, placeholder]) => {
        const element = elements.create(type, { ...cardStyle, placeholder });
        element.on('change', event => setDone(current => ({ ...current, [key]: event.complete })));
        if (fieldRefs[key].current) element.mount(fieldRefs[key].current!);
        if (key === 'number') cardNumber.current = element;
        mounted.push(element);
      });
      setSession({ clientSecret: value.clientSecret, stripe });
    }).catch(startError => { if (active) setError(getUserFacingErrorText(startError, AUTO_RENEWAL_ERROR_TEXT.startFailed)); });
    return () => { active = false; mounted.forEach(element => element.destroy()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const incomplete = !name.trim() || !done.number || !done.expiry || !done.cvc;
  const save = async () => {
    setTried(true); setError(undefined);
    if (!confirmedSetupIntent.current && (incomplete || !session || !cardNumber.current)) return;
    setSaving(true);
    try {
      if (!confirmedSetupIntent.current) {
        const result = await session!.stripe.confirmCardSetup(session!.clientSecret, {
          payment_method: { card: cardNumber.current, billing_details: { name: name.trim() } },
        });
        if (result.error || !result.setupIntent) {
          setError(AUTO_RENEWAL_ERROR_TEXT.cardNotSaved);
          setSaving(false);
          return;
        }
        confirmedSetupIntent.current = result.setupIntent.id;
      }
      await complete({ variables: { input: { setupIntentId: confirmedSetupIntent.current!, turnOnAutoRenewal } } });
      setSaving(false);
      onSaved();
    } catch (saveError) {
      setError(getUserFacingErrorText(saveError, AUTO_RENEWAL_ERROR_TEXT.saveFailed));
      setSaving(false);
    }
  };

  return (
    // MD-I9 (ACC:1511, 1537): only the ✕ or Cancel closes the pop-up.
    <Dialog open maxWidth={false} aria-labelledby="mi-card-title"
      PaperProps={{ style: { width: 'min(700px, 94vw)', maxHeight: '92vh', borderRadius: 16, margin: 0, position: 'relative', fontFamily: "'DM Sans', sans-serif", color: TEXT } }}>
      <button type="button" aria-label="Close" disabled={saving} onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, width: 28, height: 28,
        borderRadius: '50%', background: RED, color: '#FFFFFF', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>✕</button>
      <div style={{ padding: '26px 32px 20px' }}>
        <div id="mi-card-title" style={{ fontSize: 22, fontWeight: 700, marginBottom: 22 }}>Update Credit Card Information</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingBottom: 16, borderBottom: '1px solid #ECECEC', marginBottom: 16 }}>
          <div><div style={smallLabel}>MEMBER</div><div style={{ fontSize: 15, fontWeight: 700 }}>{memberName}</div></div>
          <div><div style={smallLabel}>CARD ON FILE</div><div style={{ fontSize: 15, fontWeight: 700 }}>{cardOnFile || ''}</div></div>
        </div>
        <div style={{ ...smallLabel, marginBottom: 10 }}>NEW CARD INFORMATION</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label><div style={{ fontSize: 13, marginBottom: 6 }}>Name on card</div>
            <input autoComplete="cc-name" disabled={saving} value={name} onChange={event => setName(event.target.value)} placeholder="Full name as shown on card" style={fieldBox} /></label>
          <div><div style={{ fontSize: 13, marginBottom: 6 }}>Card number</div><div ref={fieldRefs.number} style={fieldBox} data-testid="card-number-field" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div><div style={{ fontSize: 13, marginBottom: 6 }}>Expiration date</div><div ref={fieldRefs.expiry} style={fieldBox} /></div>
            <div><div style={{ fontSize: 13, marginBottom: 6 }}>Security code</div><div ref={fieldRefs.cvc} style={fieldBox} /></div>
          </div>
          {tried && incomplete && !confirmedSetupIntent.current && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{CARD_FIELDS_INCOMPLETE}</div>}
          {error && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{error}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, padding: '16px 32px 20px', background: '#F5F9FD', borderRadius: '0 0 16px 16px' }}>
        <button type="button" onClick={save} disabled={saving || !session} style={{ flex: 1, background: BLUE, color: '#FFFFFF', border: 'none', borderRadius: 8,
          padding: '13px 0', fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700, cursor: saving || !session ? 'default' : 'pointer', opacity: saving || !session ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Credit Card Information'}
        </button>
      </div>
    </Dialog>
  );
};

export default function MemberAutoRenewal() {
  const query = useGetMyCmpMemberAutoRenewalQuery({ fetchPolicy: 'network-only', context: operationContext });
  const [setAutoRenewal, setState] = useSetMyCmpMemberAutoRenewalMutation({ context: operationContext });
  const data = query.data?.getMyCmpMemberAutoRenewal;
  const [checked, setChecked] = useState(false);
  const [modal, setModal] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => { if (data) setChecked(data.autoRenew); }, [data]);
  if (!data?.available) return null;

  const toggle = async () => {
    const next = !checked;
    setError(undefined);
    setChecked(next);
    // Without a saved card the box only enables the button; the card save turns auto-renewal on.
    if (next && !data.cardOnFile) return;
    if (next === data.autoRenew) return;
    try {
      await setAutoRenewal({ variables: { input: { autoRenew: next } } });
      await query.refetch();
    } catch (toggleError) {
      setChecked(data.autoRenew);
      setError(getUserFacingErrorText(toggleError, AUTO_RENEWAL_ERROR_TEXT.toggleFailed));
    }
  };

  return (
    <div className="mi-auto-renewal" style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #ECECEC' }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} disabled={setState.loading} onChange={toggle} aria-label="Select Auto Renewal"
          className="cmp-check" style={{ margin: '1px 0 0', width: 18, height: 18 }} />
        <span style={{ fontSize: 14, color: TEXT, lineHeight: 1.55 }}><b>Select Auto Renewal</b> {AUTO_RENEWAL_TEXT}</span>
      </label>
      <button type="button" disabled={!checked} onClick={() => setModal(true)}
        style={{ margin: '16px 0 0 28px', background: checked ? BLUE : '#CFCFCF', color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '11px 22px',
          fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: checked ? 'pointer' : 'not-allowed' }}>
        Update Credit Card Information
      </button>
      {error && <p role="alert" style={{ margin: '10px 0 0 28px', fontSize: 13, fontWeight: 600, color: RED }}>{error}</p>}
      {modal && <CardModal memberName={data.memberName} cardOnFile={data.cardOnFile?.label} turnOnAutoRenewal={checked}
        onClose={() => setModal(false)}
        onSaved={() => { setModal(false); void query.refetch(); }} />}
    </div>
  );
}
